import type { PageHelp } from '../../help/types';

/** Guide for `/projects` — the project list and the "New project" dialog. */
export const projectsHelp: PageHelp = {
  title: 'Projects',
  intro:
    'Every project you can reach, with its source locale, key count and your roles on it. From here you open a project’s keys or its settings; administrators also create new projects here.',
  sections: ['The list', 'New project'],
  contexts: {
    'create-dialog': 'Click “New project” at the top right (administrators only).',
  },
  tips: [
    'Projects are normally onboarded from a configuration file by a developer (`scripts/onboard_project.py`). The dialog here creates a bare project with only its source locale; everything else is set in the project’s settings.',
  ],
  steps: [
    // ── The list ────────────────────────────────────────────────────────────────
    {
      selector: '[data-help="projects-search"]',
      title: 'Find a project',
      body: 'Filters the list as you type, matching the project code or its name. The filter is not kept — it is empty again the next time you open the page.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Ein Projekt finden',
          body: 'Filtert die Liste während der Eingabe nach Projektcode oder Name. Der Filter wird nicht gespeichert — beim nächsten Öffnen der Seite ist er wieder leer.',
        },
        'sr-Latn-RS': {
          title: 'Pronalaženje projekta',
          body: 'Filtrira listu dok kucate, po kodu ili nazivu projekta. Filter se ne pamti — kada sledeći put otvorite stranicu, ponovo je prazan.',
        },
      },
    },
    {
      selector: '[data-help="projects-list"]',
      title: 'Read the project list',
      body: 'One row per project with its code, name, source locale and number of keys. The code is also the project’s own key prefix, so the project `rep` owns the keys named `rep.…`.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Die Projektliste lesen',
          body: 'Eine Zeile pro Projekt mit Code, Name, Ausgangssprache und Anzahl der Keys. Der Code ist zugleich das eigene Key-Präfix des Projekts — das Projekt `rep` besitzt also die Keys mit dem Namen `rep.…`.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje liste projekata',
          body: 'Jedan red za svaki projekat, sa kodom, nazivom, izvornim jezikom i brojem ključeva. Kod je ujedno i sopstveni prefiks ključeva projekta, pa projekat `rep` poseduje ključeve sa nazivom `rep.…`.',
        },
      },
    },
    {
      selector: '[data-help="projects-roles"]',
      title: 'Check what you may do',
      body: 'Shows your grants on each project: owner, translator, developer or viewer. “admin access” means you hold no grant there but reach the project as an administrator, which allows everything.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Prüfen, was Sie dürfen',
          body: 'Zeigt Ihre Berechtigungen in jedem Projekt: owner, translator, developer oder viewer. „admin access“ bedeutet, dass Sie dort keine eigene Berechtigung haben, das Projekt aber als Administrator erreichen — und damit alles dürfen.',
        },
        'sr-Latn-RS': {
          title: 'Provera onoga što smete',
          body: 'Prikazuje vaša ovlašćenja na svakom projektu: owner, translator, developer ili viewer. „admin access“ znači da tamo nemate posebno ovlašćenje, ali projektu pristupate kao administrator, što dozvoljava sve.',
        },
      },
    },
    {
      selector: '[data-help="projects-open-keys"]',
      title: 'Open the keys of a project',
      body: '“Keys” opens the translation editor for that project and makes it the current project in the switcher at the top, so the Project links in the menu lead to it.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Keys eines Projekts öffnen',
          body: '„Keys“ öffnet den Übersetzungseditor dieses Projekts und macht es in der Projektauswahl oben zum aktuellen Projekt, sodass die Projekt-Links im Menü dorthin führen.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje ključeva projekta',
          body: '„Keys“ otvara editor prevoda za taj projekat i postavlja ga kao trenutni projekat u biraču na vrhu, pa linkovi iz grupe Project u meniju vode na njega.',
        },
      },
    },
    {
      selector: '[data-help="projects-open-settings"]',
      title: 'Open the project settings',
      body: '“Settings” opens the project’s locales, package subscriptions, publish targets and export options. Everyone with access can read them; only the project owner and administrators can change them.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Projekteinstellungen öffnen',
          body: '„Settings“ öffnet Sprachen, Paket-Abonnements, Veröffentlichungsziele und Exportoptionen des Projekts. Jeder mit Zugriff kann sie lesen; ändern können sie nur der Projekt-Owner und Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje podešavanja projekta',
          body: '„Settings“ otvara jezike, pretplate na pakete, ciljeve objavljivanja i opcije izvoza projekta. Svako ko ima pristup može da ih pročita; menjati ih mogu samo vlasnik projekta (owner) i administratori.',
        },
      },
    },

    // ── New project ─────────────────────────────────────────────────────────────
    {
      selector: '[data-help="projects-create"]',
      title: 'Create a project',
      body: 'Opens the “New project” dialog. Only administrators see this button, and the server refuses a new project from anybody else.',
      section: 'New project',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Ein Projekt anlegen',
          body: 'Öffnet den Dialog „New project“. Nur Administratoren sehen diese Schaltfläche, und der Server lehnt ein neues Projekt von allen anderen ab.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje projekta',
          body: 'Otvara dijalog „New project“. Ovo dugme vide samo administratori, a server odbija novi projekat od bilo koga drugog.',
        },
      },
    },
    {
      selector: '[data-help="projects-create-code"]',
      title: 'Choose the project code',
      body: 'Lower-case letters, digits and dashes, 2 to 10 characters, starting with a letter. The code becomes the project’s own key prefix, so it is refused when another project uses the same code or another project or package already owns that prefix.',
      section: 'New project',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Den Projektcode wählen',
          body: 'Kleinbuchstaben, Ziffern und Bindestriche, 2 bis 10 Zeichen, beginnend mit einem Buchstaben. Der Code wird zum eigenen Key-Präfix des Projekts; er wird daher abgelehnt, wenn ein anderes Projekt denselben Code hat oder ein anderes Projekt oder Paket dieses Präfix bereits besitzt.',
        },
        'sr-Latn-RS': {
          title: 'Izbor koda projekta',
          body: 'Mala slova, cifre i crtice, od 2 do 10 znakova, sa slovom na početku. Kod postaje sopstveni prefiks ključeva projekta, pa se odbija ako drugi projekat već ima isti kod ili ako taj prefiks već poseduje drugi projekat ili paket.',
        },
      },
    },
    {
      selector: '[data-help="projects-create-name"]',
      title: 'Name the project',
      body: 'The display name shown on the dashboard, in this list and as the title of the project’s settings. Required, up to 100 characters.',
      section: 'New project',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Das Projekt benennen',
          body: 'Der Anzeigename im Dashboard, in dieser Liste und als Titel der Projekteinstellungen. Pflichtfeld, höchstens 100 Zeichen.',
        },
        'sr-Latn-RS': {
          title: 'Imenovanje projekta',
          body: 'Naziv koji se prikazuje na Dashboard-u, na ovoj listi i kao naslov podešavanja projekta. Obavezno polje, najviše 100 znakova.',
        },
      },
    },
    {
      selector: '[data-help="projects-create-source-locale"]',
      title: 'Pick the source locale',
      body: 'The language keys are written in first, `en-GB` unless you change it. A key with no value in this locale is never exported, in any language. The project starts with this one locale; add the others in its settings.',
      section: 'New project',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Die Ausgangssprache wählen',
          body: 'Die Sprache, in der Keys zuerst geschrieben werden — `en-GB`, sofern Sie nichts anderes wählen. Ein Key ohne Wert in dieser Sprache wird in keiner Sprache exportiert. Das Projekt beginnt mit genau dieser Sprache; die übrigen fügen Sie in den Projekteinstellungen hinzu.',
        },
        'sr-Latn-RS': {
          title: 'Izbor izvornog jezika',
          body: 'Jezik na kojem se ključevi prvo pišu — `en-GB`, osim ako ga ne promenite. Ključ bez vrednosti na ovom jeziku nikada se ne izvozi, ni na jednom jeziku. Projekat počinje samo sa ovim jezikom; ostale dodajte u podešavanjima projekta.',
        },
      },
    },
    {
      selector: '[data-help="projects-create-submit"]',
      title: 'Create the project',
      body: '“Create” checks the form and saves the project, which then appears in this list. When the server refuses — a code or prefix that is already taken — the reason is shown at the top of the dialog and next to the field it concerns.',
      section: 'New project',
      context: 'create-dialog',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Das Projekt anlegen',
          body: '„Create“ prüft das Formular und speichert das Projekt, das danach in dieser Liste erscheint. Lehnt der Server ab — etwa weil Code oder Präfix bereits vergeben sind —, steht der Grund oben im Dialog und beim betroffenen Feld.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje projekta',
          body: '„Create“ proverava formular i čuva projekat, koji se zatim pojavljuje na ovoj listi. Ako server odbije — na primer zato što su kod ili prefiks već zauzeti — razlog se prikazuje na vrhu dijaloga i pored polja na koje se odnosi.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Projekte',
      intro:
        'Alle Projekte, auf die Sie Zugriff haben, mit Ausgangssprache, Anzahl der Keys und Ihren Rollen. Von hier öffnen Sie die Keys oder die Einstellungen eines Projekts; Administratoren legen hier auch neue Projekte an.',
      sections: {
        'The list': 'Die Liste',
        'New project': 'Neues Projekt',
      },
      contexts: {
        'create-dialog': 'Klicken Sie rechts oben auf „New project“ (nur für Administratoren).',
      },
      tips: [
        'Projekte werden normalerweise von einem Entwickler aus einer Konfigurationsdatei angelegt (`scripts/onboard_project.py`). Der Dialog hier legt ein leeres Projekt mit nur der Ausgangssprache an; alles Weitere stellen Sie in den Projekteinstellungen ein.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Projekti',
      intro:
        'Svi projekti kojima imate pristup, sa izvornim jezikom, brojem ključeva i vašim ulogama. Odavde otvarate ključeve ili podešavanja projekta; administratori ovde i kreiraju nove projekte.',
      sections: {
        'The list': 'Lista',
        'New project': 'Novi projekat',
      },
      contexts: {
        'create-dialog': 'Kliknite na „New project“ u gornjem desnom uglu (samo za administratore).',
      },
      tips: [
        'Projekte obično uvodi developer iz konfiguracione datoteke (`scripts/onboard_project.py`). Dijalog ovde kreira prazan projekat samo sa izvornim jezikom; sve ostalo se podešava u podešavanjima projekta.',
      ],
    },
  },
};
